import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

function buildQueryStringFromParams(
  params: URLSearchParams,
  paramsUpdates?: Record<string, string | undefined | null>,
) {
  const mergedParams = [
    ...params.entries(),
    ...(paramsUpdates ? Object.entries(paramsUpdates) : []),
  ].reduce((acc, [key, value]) => {
    acc.set(key, value);
    return acc;
  }, new Map());
  return Array.from(mergedParams.entries()).reduce(
    (acc, [key, value]) =>
      value !== undefined && value !== null
        ? `${acc}${acc.length ? '&' : ''}${key}=${value}`
        : acc,
    '',
  );
}

/**
 * A custom hook to manage query parameters in the URL.
 * @returns - Methods to add, remove, and set query parameters.
 */
export function useQueryParams() {
  const router = useRouter();
  const searchParams = useSearchParams();

  /**
   * Adds or updates a query parameter in the URL.
   * @param key - The key of the query parameter.
   * @param value - The value of the query parameter.
   */
  const addQueryParam = useCallback(
    (key: string, value: string, options?: { skipEncoding?: boolean }) => {
      const params = new URLSearchParams(
        searchParams ? Array.from(searchParams.entries()) : {},
      );
      if (options?.skipEncoding) {
        const paramString = buildQueryStringFromParams(params, {
          [key]: value,
        });
        router.replace(`?${paramString}`, { scroll: false });
      } else {
        params.set(key, value);
        router.replace(`?${params.toString()}`, { scroll: false });
      }
    },
    [router, searchParams],
  );

  /**
   * Removes a query parameter from the URL.
   * @param key - The key of the query parameter to remove.
   */
  const removeQueryParam = useCallback(
    async (key: string, options?: { skipEncoding?: boolean }) => {
      if (searchParams) {
        const params = new URLSearchParams(searchParams);
        params.delete(key);
        const paramsString = options?.skipEncoding
          ? buildQueryStringFromParams(params)
          : params.toString();
        router.replace(`${paramsString ? '?' : ''}${paramsString}`, {
          scroll: false,
        });
      }
    },
    [router, searchParams],
  );

  /**
   * Replaces the query parameters with the provided object.
   * @param newParams - An object representing the new query parameters.
   */
  const setQueryParams = useCallback(
    (newParams: Record<string, string>) => {
      const params = new URLSearchParams(newParams);
      router.replace(`?${params.toString()}`, { scroll: false });
    },
    [router],
  );

  /**
   * Set or remove query params based on values in provided object
   * (undefined/null values removed).
   * @param paramsUpdates - An object representing the query parameters updates
   */
  const updateQueryParams = useCallback(
    (
      paramsUpdates: Record<string, string | undefined | null>,
      options?: { skipEncoding?: boolean },
    ) => {
      const params = new URLSearchParams(searchParams ?? {});
      if (options?.skipEncoding) {
        const paramString = buildQueryStringFromParams(params, paramsUpdates);
        router.replace(`?${paramString}`, { scroll: false });
      } else {
        Object.entries(paramsUpdates).forEach(([key, value]) => {
          if (value) {
            params.set(key, value);
          } else {
            params.delete(key);
          }
        });
        router.replace(`?${params.toString()}`, { scroll: false });
      }
    },
    [router, searchParams],
  );

  return { addQueryParam, removeQueryParam, setQueryParams, updateQueryParams };
}
